import React, { useState } from 'react';
import { Paper, Box, TextField, Grid, Typography, Button, Card, CardContent, IconButton, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { prepareFieldUpdates } from '../utils/fieldUtils';

const CareersTab = ({ leadingContent }) => {
  const { t } = useTranslation();
  const { config, handleChange, handleAddArrayItem, handleArrayChange, handleRemoveArrayItem, isFieldConfigurable, isFieldFixed, loadConfig } = useSitesConfig();
  const [saving, setSaving] = useState(false);

  const careersPath = 'siteConfig.careers';
  const positionsPath = 'siteConfig.careers.positions';
  const isTitleConfigurable = isFieldConfigurable(`${careersPath}.title`);
  const isSubtitleConfigurable = isFieldConfigurable(`${careersPath}.subtitle`);
  const isDescriptionConfigurable = isFieldConfigurable(`${careersPath}.description`);
  const isApplyTextConfigurable = isFieldConfigurable(`${careersPath}.applyText`);
  const isNoPositionsTextConfigurable = isFieldConfigurable(`${careersPath}.noPositionsText`);
  const isSendCvCtaTextConfigurable = isFieldConfigurable(`${careersPath}.sendCvCtaText`);
  const isSendCvCtaButtonTextConfigurable = isFieldConfigurable(`${careersPath}.sendCvCtaButtonText`);
  const isPositionsConfigurable = isFieldConfigurable(positionsPath);
  const isPositionsFixed = isFieldFixed(positionsPath);
  
  const hasEditableFields = isTitleConfigurable || isSubtitleConfigurable || isDescriptionConfigurable || 
    isApplyTextConfigurable || isNoPositionsTextConfigurable || isSendCvCtaTextConfigurable || isSendCvCtaButtonTextConfigurable || (isPositionsConfigurable && !isPositionsFixed);
  
  if (!hasEditableFields) {
    return (
      <Paper sx={{ padding: 3, mt: 2 }}>
        <Alert severity="info">
          {t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual'}
        </Alert>
      </Paper>
    );
  }

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const updates = {};
      
      // Campos simples
      if (isTitleConfigurable) {
        updates[`${careersPath}.title`] = config.siteConfig.careers.title;
      }
      if (isSubtitleConfigurable) {
        updates[`${careersPath}.subtitle`] = config.siteConfig.careers.subtitle;
      }
      if (isDescriptionConfigurable) {
        updates[`${careersPath}.description`] = config.siteConfig.careers.description;
      }
      if (isApplyTextConfigurable) {
        updates[`${careersPath}.applyText`] = config.siteConfig.careers.applyText;
      }
      if (isNoPositionsTextConfigurable) {
        updates[`${careersPath}.noPositionsText`] = config.siteConfig.careers.noPositionsText;
      }
      if (isSendCvCtaTextConfigurable) {
        updates[`${careersPath}.sendCvCtaText`] = config.siteConfig.careers.sendCvCtaText ?? '';
      }
      if (isSendCvCtaButtonTextConfigurable) {
        updates[`${careersPath}.sendCvCtaButtonText`] = config.siteConfig.careers.sendCvCtaButtonText ?? '';
      }
      
      // Array de positions
      if (isPositionsConfigurable && !isPositionsFixed) {
        updates[positionsPath] = config.siteConfig.careers.positions;
      }

      if (Object.keys(updates).length === 0) {
        SnackbarUtils.warning(t('no_editable_fields') || 'No hay campos editables en esta sección');
        return;
      }

      const preparedUpdates = prepareFieldUpdates(updates);
      await SitesService.updateFields(preparedUpdates);
      await loadConfig();
      
      SnackbarUtils.success(t('section_saved_successfully') || 'Sección guardada exitosamente');
    } catch (err) {
      console.error('Error saving careers:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_saving_section') || 'Error al guardar la sección'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      {leadingContent}
      <Grid container spacing={2}>
        {isTitleConfigurable && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('careers_title') || 'Título'}
              value={config.siteConfig.careers.title || ''}
              onChange={(e) => handleChange('siteConfig.careers.title', e.target.value)}
            />
          </Grid>
        )}
        {isSubtitleConfigurable && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('careers_subtitle') || 'Subtítulo'}
              value={config.siteConfig.careers.subtitle || ''}
              onChange={(e) => handleChange('siteConfig.careers.subtitle', e.target.value)}
            />
          </Grid>
        )}
        {isDescriptionConfigurable && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={t('careers_description') || 'Descripción'}
              value={config.siteConfig.careers.description || ''}
              onChange={(e) => handleChange('siteConfig.careers.description', e.target.value)}
            />
          </Grid>
        )}
        {isApplyTextConfigurable && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('careers_apply_text') || 'Texto del Botón Aplicar'}
              value={config.siteConfig.careers.applyText || ''}
              onChange={(e) => handleChange('siteConfig.careers.applyText', e.target.value)}
            />
          </Grid>
        )}
        {isNoPositionsTextConfigurable && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('careers_no_positions_text') || 'Texto Sin Posiciones'}
              value={config.siteConfig.careers.noPositionsText || ''}
              onChange={(e) => handleChange('siteConfig.careers.noPositionsText', e.target.value)}
            />
          </Grid>
        )}
        {isSendCvCtaTextConfigurable && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('careers_send_cv_cta_text') || 'Texto CTA (¿No encuentras lo que buscas?)'}
              value={config.siteConfig.careers.sendCvCtaText ?? ''}
              onChange={(e) => handleChange('siteConfig.careers.sendCvCtaText', e.target.value)}
              placeholder="¿No encuentras lo que buscas? Envíanos tu CV igualmente."
            />
          </Grid>
        )}
        {isSendCvCtaButtonTextConfigurable && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('careers_send_cv_cta_button_text') || 'Texto del botón CTA'}
              value={config.siteConfig.careers.sendCvCtaButtonText ?? ''}
              onChange={(e) => handleChange('siteConfig.careers.sendCvCtaButtonText', e.target.value)}
              placeholder="Postularse para una vacante"
            />
          </Grid>
        )}
        {isPositionsConfigurable && !isPositionsFixed && (
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleAddArrayItem('siteConfig.careers.positions', {
                  id: Date.now().toString(),
                  title: '',
                  type: '',
                  location: '',
                  description: ''
                })}
                variant="outlined"
              >
                {t('add_position') || 'Agregar Posición'}
              </Button>
            </Box>
            {config.siteConfig.careers.positions?.map((position, index) => (
              <Card key={position.id || index} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="subtitle1">{t('position') || 'Posición'} {index + 1}</Typography>
                    <IconButton
                      onClick={() => handleRemoveArrayItem('siteConfig.careers.positions', index)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('position_title') || 'Título'}
                        value={position.title || ''}
                        onChange={(e) => handleArrayChange('siteConfig.careers.positions', index, 'title', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('position_type') || 'Tipo'}
                        value={position.type || ''}
                        onChange={(e) => handleArrayChange('siteConfig.careers.positions', index, 'type', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('position_location') || 'Ubicación'}
                        value={position.location || ''}
                        onChange={(e) => handleArrayChange('siteConfig.careers.positions', index, 'location', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label={t('position_description') || 'Descripción'}
                        value={position.description || ''}
                        onChange={(e) => handleArrayChange('siteConfig.careers.positions', index, 'description', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Grid>
        )}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
          {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
        </Button>
      </Box>
    </Paper>
  );
};

export default CareersTab;
